import CustomButton from 'Components/CustomButton'
import DrawerRight from 'Components/DrawerRight'
import { WithLoader } from 'Components/WithLoader'
import { CatalogProductsEdit } from 'Services/ProductManagement/Features/Catalog/Components/CatalogProductsEdit'
import { useGetSingleCatalogQuery } from 'Services/ProductManagement/Features/Catalog/catalogApi'
import useCatalogsWithParams from 'Services/ProductManagement/Features/Catalog/Hooks/useCatalogsWithParams'
import {
  useGetProductGroupParameterGroupQuery,
  useGetSalesChannelParameterGroupQuery
} from 'Services/ProductManagement/Features/Parameter/parameterApi'
import { fetchAllTariffCatalogProducts } from 'Services/ProductManagement/Features/Product/productSlice'
import TariffCatalogAllowedApiClients from 'Services/ProductManagement/Features/Tariff/Components/TariffCatalogApiClients'
import { TariffCatalogDetails } from 'Services/ProductManagement/Features/Tariff/Components/TariffCatalogDetails'
import {
  type Tariff,
  TariffCatalogTabType
} from 'Services/ProductManagement/Features/Tariff/tariffSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import { All_TARIFF_PRODUCTS_COUNT_PER_REQUEST, PRODUCT_MANAGEMENT_ROOT_URL } from 'Util/constants'
import getTranslation from 'Util/getTranslation'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab } from '@mui/material'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

export const DRAWER_TAB_PARAM_KEY = 'drawerTab'

interface TariffCatalogDrawerProps {
  open: boolean
  handleClose: () => void
  getProductCount: (catalogProductIds: string[]) => number | '-'
  selectedCatalogId: string | undefined
}

export function TariffCatalogDrawer({
  open,
  handleClose,
  getProductCount,
  selectedCatalogId
}: Readonly<TariffCatalogDrawerProps>) {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { id: tariffId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const drawerTabFromParams =
    searchParams.get(DRAWER_TAB_PARAM_KEY) ?? TariffCatalogTabType.PRODUCTS

  const { params, setParams } = useCatalogsWithParams()
  const { data: selectedCatalog, isFetching: isSingleCatalogFetching } = useGetSingleCatalogQuery(
    selectedCatalogId ?? '',
    {
      skip: !selectedCatalogId
    }
  )
  const { singleTariff, singleLoading: singleTariffLoading } = useTypedSelector(
    state => state.tariff
  )
  const {
    data: productGroupParameterGroup = null,
    isLoading: isProductGroupParameterGroupLoading
  } = useGetProductGroupParameterGroupQuery(undefined, {
    skip: !open
  })

  const {
    data: salesChannelParameterGroups = null,
    isFetching: isSalesChannelParameterGroupsFetching
  } = useGetSalesChannelParameterGroupQuery(undefined, {
    refetchOnMountOrArgChange: true
  })

  const handleDrawerTabChange = (_event: React.SyntheticEvent, newValue: TariffCatalogTabType) => {
    searchParams.set(DRAWER_TAB_PARAM_KEY, newValue)
    setSearchParams(searchParams)
  }

  const handleCatalogProductsSaveSuccess = () => {
    if (!selectedCatalog) return

    setParams({ ...params, tariff_id: tariffId })
    dispatch(
      fetchAllTariffCatalogProducts({
        in_tariff: tariffId,
        in_catalog: selectedCatalog.id,
        bypass_pagination: true,
        count: All_TARIFF_PRODUCTS_COUNT_PER_REQUEST
      })
    )
  }

  const productGroupOptions = productGroupParameterGroup?.parameters.map(
    ({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value,
      value: parameter_id
    })
  )

  // Add "All ticket types" option to the start of the array
  productGroupOptions?.unshift({ label: t`All ticket types`, value: '' })

  const isSingleTariffLoading = singleTariffLoading === 'pending'

  return (
    <DrawerRight
      open={open}
      onClose={handleClose}
      topBar={
        <Box>
          {selectedCatalog && (
            <CustomButton
              onClick={() =>
                navigate(`${baseUrl}/${tariffId}/sales-channels/${selectedCatalog.id}/edit`)
              }
              size="small"
              startIcon={<EditIcon />}
            >{t`Edit`}</CustomButton>
          )}
        </Box>
      }
    >
      <TabContext value={drawerTabFromParams}>
        <TabList onChange={handleDrawerTabChange} aria-label={t`Tariff sales channel tab list`}>
          <Tab label={t`Sales channel products`} value={TariffCatalogTabType.PRODUCTS} />
          <Tab label={t`Sales channel details`} value={TariffCatalogTabType.DETAILS} />
          <Tab label={t`Allowed API clients`} value={TariffCatalogTabType.ALLOWED_API_CLIENTS} />
        </TabList>
        <Divider />
        <TabPanel sx={{ p: 0 }} value={TariffCatalogTabType.PRODUCTS}>
          <WithLoader loading={isProductGroupParameterGroupLoading || isSingleTariffLoading}>
            {selectedCatalog && (
              <CatalogProductsEdit
                singleTariff={singleTariff as Tariff}
                selectedCatalog={selectedCatalog}
                productGroupOptions={productGroupOptions}
                handleCancel={handleClose}
                handleSaveSuccess={handleCatalogProductsSaveSuccess}
              />
            )}
          </WithLoader>
        </TabPanel>
        <TabPanel sx={{ p: 0 }} value={TariffCatalogTabType.DETAILS}>
          <WithLoader loading={isSingleTariffLoading}>
            {selectedCatalog && (
              <TariffCatalogDetails
                catalog={selectedCatalog}
                tariff={singleTariff as Tariff}
                singleLoading={isSingleCatalogFetching}
                getProductCount={getProductCount}
                salesChannelParameterGroup={salesChannelParameterGroups}
                isSalesChannelParameterGroupLoading={isSalesChannelParameterGroupsFetching}
              />
            )}
          </WithLoader>
        </TabPanel>
        <TabPanel sx={{ p: 0 }} value={TariffCatalogTabType.ALLOWED_API_CLIENTS}>
          {selectedCatalog && (
            <TariffCatalogAllowedApiClients catalog={selectedCatalog} handleClose={handleClose} />
          )}
        </TabPanel>
      </TabContext>
    </DrawerRight>
  )
}
